import { useState, useMemo } from "react";
import SEO from "@/components/feature/SEO";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAdminData } from "@/hooks/useAdmin";
import {
  Search,
  CalendarDays,
  Clock,
  Phone,
  Mail,
  MapPin,
  ArrowUpDown,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  Download,
  Globe,
} from "lucide-react";

const statusOptions = [
  "all",
  "scheduled",
  "confirmed",
  "completed",
  "cancelled",
  "no_show",
] as const;

export default function AdminAppointmentsPage() {
  const { appointments, updateAppointmentStatus, loading, error } = useAdminData();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

  const sourceOptions = useMemo(() => {
    const sources = new Set<string>();
    appointments.forEach((a) => {
      if (a.source) sources.add(a.source);
    });
    return ["all", ...Array.from(sources).sort()];
  }, [appointments]);

  const filtered = appointments
    .filter((a) => {
      const matchesSearch =
        search === "" ||
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase()) ||
        a.phone.includes(search) ||
        a.city.toLowerCase().includes(search.toLowerCase()) ||
        (a.source && a.source.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus = statusFilter === "all" || a.status === statusFilter;
      const matchesSource = sourceFilter === "all" || a.source === sourceFilter;
      return matchesSearch && matchesStatus && matchesSource;
    })
    .sort((a, b) => {
      const dateA = new Date(`${a.preferred_date}T${a.preferred_time}`).getTime();
      const dateB = new Date(`${b.preferred_date}T${b.preferred_time}`).getTime();
      return sortDir === "asc" ? dateA - dateB : dateB - dateA;
    });

  const statusBadge: Record<string, string> = {
    scheduled: "bg-amber-100 text-amber-700",
    confirmed: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700",
    no_show: "bg-slate-100 text-slate-600",
  };

  const exportToCSV = () => {
    const headers = [
      "ID", "Name", "Email", "Phone", "City", "Insurance Type", "Preferred Date", "Preferred Time", "Source", "Status", "Notes", "Created At"
    ];
    const rows = filtered.map((apt) => [
      apt.id,
      apt.name,
      apt.email,
      apt.phone,
      apt.city,
      apt.insurance_type,
      apt.preferred_date,
      apt.preferred_time,
      apt.source || "",
      apt.status,
      apt.notes || "",
      new Date(apt.created_at).toLocaleString("en-IN"),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `appointments-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <SEO title="Appointments | ExpertAdvise.in Admin" noindex />
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">
            Appointments
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage scheduled consultations. {filtered.length} of {appointments.length} appointments shown.
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            Failed to load appointments: {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4 md:mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search appointments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="all">All Status</option>
            {statusOptions
              .filter((s) => s !== "all")
              .map((s) => (
                <option key={s} value={s}>
                  {s
                    .split("_")
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(" ")}
                </option>
              ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="all">All Sources</option>
            {sourceOptions
              .filter((s) => s !== "all")
              .map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
          </select>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <button
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            className="flex items-center gap-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            {sortDir === "asc" ? "Soonest First" : "Latest First"}
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500 text-sm">
            Loading appointments...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((apt) => (
              <div
                key={apt.id}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-slate-900">{apt.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{apt.insurance_type}</div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                      statusBadge[apt.status] || "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {apt.status.split("_").join(" ")}
                  </span>
                </div>

                <div className="space-y-1.5 text-xs text-slate-500 mb-4">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                    {apt.preferred_date}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    {apt.preferred_time}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    {apt.city}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    {apt.phone}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    <span className="truncate">{apt.email}</span>
                  </div>
                  {apt.source && (
                    <div className="flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-primary-600 font-medium">{apt.source}</span>
                    </div>
                  )}
                </div>

                {apt.notes && (
                  <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2.5 mb-3">
                    {apt.notes}
                  </div>
                )}

                <div className="flex items-center gap-2 relative">
                  {apt.status === "scheduled" && (
                    <button
                      onClick={() => updateAppointmentStatus(apt.id, "confirmed")}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-2 rounded-lg transition-colors flex items-center justify-center gap-1"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Confirm
                    </button>
                  )}
                  {apt.status === "confirmed" && (
                    <button
                      onClick={() => updateAppointmentStatus(apt.id, "completed")}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold py-2 rounded-lg transition-colors flex items-center justify-center gap-1"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Complete
                    </button>
                  )}
                  {["scheduled", "confirmed"].includes(apt.status) && (
                    <button
                      onClick={() => updateAppointmentStatus(apt.id, "cancelled")}
                      className="px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() =>
                      setActionMenuOpen(actionMenuOpen === apt.id ? null : apt.id)
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {actionMenuOpen === apt.id && (
                    <div className="absolute right-0 bottom-full mb-1 w-36 bg-white rounded-lg border border-slate-200 shadow-lg z-20 py-1">
                      {statusOptions
                        .filter(
                          (s) => s !== "all" && s !== apt.status
                        )
                        .map((s) => (
                          <button
                            key={s}
                            onClick={() => {
                              updateAppointmentStatus(apt.id, s);
                              setActionMenuOpen(null);
                            }}
                            className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            Mark{" "}
                            {s
                              .split("_")
                              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                              .join(" ")}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500 text-sm bg-white rounded-xl border border-slate-200">
            No appointments match your filters.
          </div>
        )}
      </div>
    </AdminLayout>
  );
}